import { useState } from 'react'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mdiReplay } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { apiClient } from '../../api/client'
import type { WebhookDelivery } from '../../api/schema'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useCan } from '../../contexts/PermissionsContext'
import { apiErrorCode } from '../../lib/apiError'
import { notify } from '../../lib/notifications'
import { useFormatters } from '../../lib/format'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { EmptyState } from '../ui/empty-state'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet'
import { Skeleton } from '../ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { useRefreshWebhooks } from './WebhooksSettingsPage'

export const DELIVERY_STATUSES = ['pending', 'delivered', 'failed', 'cancelled'] as const
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number]
const ALL = '__all__'

const STATUS_VARIANT: Record<DeliveryStatus, 'warning' | 'success' | 'destructive' | 'secondary'> = {
    pending: 'warning',
    delivered: 'success',
    failed: 'destructive',
    cancelled: 'secondary',
}

function StatusBadge({ status }: { status: DeliveryStatus }) {
    const { t } = useTranslation('webhooks')
    return <Badge variant={STATUS_VARIANT[status]} data-testid="webhook-delivery-status">{t(`status.${status}`)}</Badge>
}

/** The delivery drawer: the envelope and the replay. */
function DeliveryDetail({ org, uid, onClose }: { org: string; uid: string | null; onClose: () => void }) {
    const { t } = useTranslation('webhooks')
    const canWrite = useCan('webhooks.write')
    const refresh = useRefreshWebhooks(org)
    const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/webhooks/deliveries/{deliveryUid}',
        { params: { path: { orgCode: org, deliveryUid: uid ?? '' } } }, { enabled: !!uid })
    const replay = $api.useMutation('post', '/api/v1/organizations/{orgCode}/webhooks/deliveries/{deliveryUid}/replay', {
        onSuccess: () => {
            refresh()
            notify({ key: 'webhooks_replay', variant: 'default', titleKey: 'webhooks:deliveries.replayed' })
        },
        onError: (err) => notify({ key: 'webhooks_error', variant: 'destructive', titleKey: `webhooks:errors.${apiErrorCode(err) ?? 'generic'}` }),
    })
    const d = data?.delivery
    const pending = d?.status === 'pending'
    return (
        <Sheet open={!!uid} onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="webhook-delivery-detail">
                <SheetHeader>
                    <SheetTitle>{t('deliveries.detail_title')}</SheetTitle>
                    <SheetDescription>{d ? t(`event_types.${d.event_type}`, { defaultValue: d.event_type }) : ''}</SheetDescription>
                </SheetHeader>
                {d && (
                    <div className="space-y-4 p-4">
                        <div className="flex items-center gap-2">
                            <StatusBadge status={d.status} />
                            <span className="text-sm text-muted-foreground">{t('deliveries.columns.attempts')}: {d.attempt_count}</span>
                        </div>
                        {d.last_error && <p className="text-sm text-destructive" data-testid="webhook-delivery-error">{d.last_error}</p>}
                        <div>
                            <Label className="mb-1 block text-sm">{t('deliveries.payload')}</Label>
                            <pre className="max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs" data-testid="webhook-delivery-payload">
                                {JSON.stringify(d.envelope, null, 2)}
                            </pre>
                        </div>
                        {canWrite && (
                            <div>
                                <Button
                                    onClick={() => replay.mutate({ params: { path: { orgCode: org, deliveryUid: d.uid } } })}
                                    disabled={pending || replay.isPending}
                                    title={pending ? t('deliveries.replay_pending') : t('deliveries.replay')}
                                    data-testid="webhook-delivery-replay-button"
                                >
                                    <AppIcon path={mdiReplay} size="sm" className="mr-1" />
                                    {t('deliveries.replay')}
                                </Button>
                                {pending && <p className="mt-1 text-xs text-muted-foreground">{t('deliveries.replay_pending')}</p>}
                            </div>
                        )}
                    </div>
                )}
            </SheetContent>
        </Sheet>
    )
}

/** The delivery log of one subscription (spec 2026-10-25-13 step 8). */
export function WebhookDeliveriesPage() {
    const { org, subscriptionUid } = useParams({ from: '/$org/settings/webhooks/$subscriptionUid' })
    const search = useSearch({ from: '/$org/settings/webhooks/$subscriptionUid' })
    const navigate = useNavigate()
    const { t } = useTranslation('webhooks')
    const { formatDate } = useFormatters()
    const [open, setOpen] = useState<string | null>(null)

    const { data: sub } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/webhooks/subscriptions/{subscriptionUid}',
        { params: { path: { orgCode: org, subscriptionUid } } })
    const label = sub?.subscription
        ? sub.subscription.description || (() => { try { return new URL(sub.subscription.url).host } catch { return sub.subscription.url } })()
        : '…'
    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('settings:page.title'), path: `/${org}/settings` },
        { title: t('title'), path: `/${org}/settings/webhooks` },
        { title: label },
    ])

    const log = useInfiniteQuery({
        queryKey: ['get', '/api/v1/organizations/{orgCode}/webhooks/subscriptions/{subscriptionUid}/deliveries',
            { params: { path: { orgCode: org, subscriptionUid }, query: { status: search.status } } }],
        initialPageParam: undefined as string | undefined,
        queryFn: async ({ pageParam }) => {
            const res = await apiClient.GET('/api/v1/organizations/{orgCode}/webhooks/subscriptions/{subscriptionUid}/deliveries', {
                params: { path: { orgCode: org, subscriptionUid }, query: { status: search.status, cursor: pageParam } },
            })
            if (res.error) throw res.error
            return res.data
        },
        getNextPageParam: (last) => last?.next_cursor ?? undefined,
        refetchInterval: 5000,
    })
    const rows: WebhookDelivery[] = (log.data?.pages ?? []).flatMap((p) => p?.deliveries ?? [])
    const isLoading = log.isLoading

    return (
        <div className="space-y-6" data-testid="webhook-deliveries-page">
            <PageHeader icon={SECTION_ICONS.webhooks} title={t('deliveries.title')} description={sub?.subscription.url ?? t('deliveries.description')} />

            <div className="max-w-xs">
                <Label className="mb-1 block text-sm">{t('deliveries.status_filter')}</Label>
                <Select
                    value={search.status ?? ALL}
                    onValueChange={(v) => {
                        navigate({ to: '/$org/settings/webhooks/$subscriptionUid', params: { org, subscriptionUid }, search: { status: v === ALL ? undefined : (v as DeliveryStatus) }, replace: true })
                    }}
                >
                    <SelectTrigger data-testid="webhook-delivery-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL} data-testid="webhook-delivery-status-filter-all">{t('deliveries.all')}</SelectItem>
                        {DELIVERY_STATUSES.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`webhook-delivery-status-filter-${s}`}>{t(`status.${s}`)}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {isLoading ? (
                <Skeleton className="h-32 w-full" />
            ) : rows.length === 0 ? (
                <EmptyState icon={SECTION_ICONS.webhooks} title={t('deliveries.empty')} data-testid="webhook-deliveries-empty-state" />
            ) : (
                <Card>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('deliveries.columns.event')}</TableHead>
                                    <TableHead>{t('deliveries.columns.subject')}</TableHead>
                                    <TableHead>{t('deliveries.columns.status')}</TableHead>
                                    <TableHead className="text-right">{t('deliveries.columns.attempts')}</TableHead>
                                    <TableHead>{t('deliveries.columns.http')}</TableHead>
                                    <TableHead>{t('deliveries.columns.error')}</TableHead>
                                    <TableHead>{t('deliveries.columns.next_attempt')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((d) => (
                                    <TableRow key={d.uid} className="cursor-pointer" onClick={() => setOpen(d.uid)} data-testid="webhook-delivery-row" data-event-type={d.event_type} data-status={d.status}>
                                        <TableCell>{t(`event_types.${d.event_type}`, { defaultValue: d.event_type })}</TableCell>
                                        <TableCell className="font-mono text-xs">{d.subject_type} {d.subject_uid.slice(0, 8)}</TableCell>
                                        <TableCell><StatusBadge status={d.status} /></TableCell>
                                        <TableCell className="text-right">{d.attempt_count}</TableCell>
                                        <TableCell>{d.last_response_status ?? '—'}</TableCell>
                                        <TableCell className="max-w-xs truncate text-sm text-destructive" title={d.last_error ?? ''} data-testid="webhook-delivery-last-error">{d.last_error ?? ''}</TableCell>
                                        <TableCell className="text-sm" data-testid="webhook-delivery-next-attempt">
                                            {d.next_attempt_at ? formatDate(d.next_attempt_at, { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
            {log.hasNextPage && (
                <Button variant="outline" onClick={() => log.fetchNextPage()} disabled={log.isFetchingNextPage} data-testid="webhook-deliveries-load-more">{t('deliveries.load_more')}</Button>
            )}

            <DeliveryDetail org={org} uid={open} onClose={() => setOpen(null)} />
        </div>
    )
}
